import type { App } from '@grafikr/components';
import header from 'layout/header';
import productDetailMedia from 'pages/product/detail-media';

import hasHash from 'utils/loaders/has-hash';
import hasRegionMatch from 'utils/loaders/has-region-match';
import intersectionLoader from 'utils/loaders/intersection';
import onDelegatedInteraction from 'utils/loaders/on-delegated-interaction';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onState from 'utils/loaders/on-state';

export default (app: App) => {
  app.add({
    // Layout
    header,
    'header-announcement': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "layout-header-announcement" */ 'layout/header-announcement'),
    ],

    // Components
    'active-state': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-active-state" */ 'components/active-state'),
    ],
    'image-carousel': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-image-carousel" */ 'components/image-carousel'),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'cart-count': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cart-count" */ 'components/cart-count'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'country-selector-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-country-selector-form" */ 'components/country-selector-form'
        ),
    ],
    'country-selector-list': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-country-selector-list" */ 'components/country-selector-list'
        ),
    ],
    'current-region': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-current-region" */ 'components/current-region'),
    ],
    'features-highlight': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-features-highlight" */ 'components/features-highlight'
        ),
    ],
    'fibbl-model': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-fibbl-model" */ 'components/fibbl-model'),
    ],
    'filter-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-filter-form" */ 'components/filter-form'),
    ],
    'fit-text': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-fit-text" */ 'components/fit-text'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    'grid-control': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-grid-control" */ 'components/grid-control'),
    ],
    marquee: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-marquee" */ 'components/marquee'),
    ],
    'mega-menu-category-filter': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-mega-menu-category-filter" */ 'components/mega-menu-category-filter'
        ),
    ],
    'media-carousel': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-media-carousel" */ 'components/media-carousel'),
    ],
    'media-carousel-fullscreen': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-media-carousel-fullscreen" */ 'components/media-carousel-fullscreen'
        ),
    ],
    'unit-system-conversion': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-unit-system-conversion" */ 'components/unit-system-conversion'
        ),
    ],
    modal: [
      [onElementInteraction, onDelegatedInteraction],
      () => import(/* webpackChunkName: "component-modal" */ 'components/modal'),
    ],
    'eu-guarantee-modal': [
      [onElementInteraction, hasHash],
      () =>
        import(
          /* webpackChunkName: "component-eu-guarantee-modal" */ 'components/eu-guarantee-modal'
        ),
    ],
    pagination: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-pagination" */ 'components/pagination'),
    ],
    'parallax-media': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-parallax-media" */ 'components/parallax-media'),
    ],
    'product-item': [
      ['mouseover', 'focusin'],
      () => import(/* webpackChunkName: "component-product-item" */ 'components/product-item'),
    ],
    'product-item-gallery': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-item-gallery" */ 'components/product-item-gallery'
        ),
    ],
    'protected-overlay-signup': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-protected-overlay-signup" */ 'components/protected-overlay-signup'
        ),
    ],
    'range-slider': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-range-slider" */ 'components/range-slider'),
    ],
    'rebuy-products': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-rebuy-products" */ 'components/rebuy-products'),
    ],
    'swap-content': [
      [onElementInteraction, onState],
      () => import(/* webpackChunkName: "component-swap-content" */ 'components/swap-content'),
    ],
    toggle: ['click', () => import(/* webpackChunkName: "component-toggle" */ 'components/toggle')],
    remove: ['click', () => import(/* webpackChunkName: "component-remove" */ 'components/remove')],
    'stacked-videos': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-stacked-videos" */ 'components/stacked-videos'),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    '3d-scroll': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-3d-scroll" */ 'components/3d-scroll'),
    ],
    'video-scroll-fullscreen': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-video-scroll-fullscreen" */ 'components/video-scroll-fullscreen'
        ),
    ],
    zendesk: [
      intersectionLoader,
      () => import(/* webpackChunkName": "component-zendesk" */ 'components/zendesk'),
    ],
    collapsible: [
      intersectionLoader,
      () => import(/* webpackCunkName: "component-collapsible" */ 'components/collapsible'),
    ],
    'size-guide-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-size-guide-drawer" */ 'components/size-guide-drawer'
        ),
    ],
    'stock-availability-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-stock-availability" */ 'components/stock-availability-drawer'
        ),
    ],
    'inventory-locations': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName": "component-inventory-locations" */ 'components/inventory-locations'
        ),
    ],
    'countdown-timer': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-countdown-timer" */ 'components/countdown-timer'),
    ],
    'overlay-password': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-overlay-password" */ 'components/overlay-password'),
    ],
    'region-switch': [
      [hasRegionMatch, onElementInteraction],
      () => import(/* webpackChunkName: "component-region-switch" */ 'components/region-switch'),
    ],

    // Pages
    cart: [intersectionLoader, () => import(/* webpackChunkName: "cart" */ 'pages/cart')],

    collection: [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-main" */ 'pages/collection/main'),
    ],
    'collection-navigation': [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-navigation" */ 'pages/collection/navigation'),
    ],
    'collection-sort': [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-sort" */ 'pages/collection/sort'),
    ],

    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-bundle': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-bundle-main" */ 'pages/product/bundle-main'),
    ],
    'product-simple': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-simple-main" */ 'pages/product/simple-main'),
    ],
    'product-comparison': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-comparison" */ 'pages/product/comparison'),
    ],
    'product-detail-media': productDetailMedia,
    'product-images': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-images" */ 'pages/product/images'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-navigation': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-navigation" */ 'pages/product/navigation'),
    ],
    'product-shipping-timer': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-shipping-timer" */ 'pages/product/shipping-timer'),
    ],
  });
};
